/**
 * MFA Verification Component
 *
 * Shown during login when user has MFA enabled.
 * Prompts for 6-digit TOTP code from authenticator app.
 *
 * Part of Phase 3: TOTP MFA for Staff Accounts
 */

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';

interface MfaVerifyProps {
  open: boolean;
  session: string;
  username: string;
  onVerify: (code: string) => Promise<void>;
  onCancel: () => void;
  error?: string;
}

export const MfaVerify = ({
  open,
  session,
  username,
  onVerify,
  onCancel,
  error,
}: MfaVerifyProps) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (code.length !== 6) {
      return;
    }

    setLoading(true);
    try {
      await onVerify(code);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && code.length === 6) {
      handleSubmit(e as any);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShieldIcon color="primary" />
          <Typography variant="h6">Two-Factor Authentication</Typography>
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1" gutterBottom>
              Enter the 6-digit code from your authenticator app
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              autoFocus
              fullWidth
              value={code}
              onChange={handleCodeChange}
              onKeyPress={handleKeyPress}
              placeholder="000000"
              disabled={loading}
              inputProps={{
                maxLength: 6,
                style: {
                  fontSize: '32px',
                  textAlign: 'center',
                  letterSpacing: '12px',
                  fontWeight: 'bold',
                },
              }}
              sx={{ mt: 3, mb: 2 }}
            />

            <Typography variant="caption" color="text.secondary">
              Codes expire every 30 seconds
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || code.length !== 6}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MfaVerify;
